// TODO: Consolidate with duplicate AnalysisReports component in FinancialPlanning domain
import React from "react";
import { useParams, useLocation } from "react-router-dom";
import Breadcrumbs from "../../../components/shared/Breadcrumbs";
import SubPageLayout from "../../../components/shared/SubPageLayout";

const AnalysisReports = () => {
  const { householdId, userId } = useParams();
  const location = useLocation();
  const isClientProfileRoute = location.pathname.includes("/client-profile/");

  // Determine breadcrumbs based on route
  const breadcrumbItems = isClientProfileRoute
    ? [
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Client Database", path: "/advisor-portal/client-database" },
        {
          label: "Household Profile",
          path: `/advisor-portal/client-database/household-profile/${householdId}`,
        },
        {
          label: "Client Profile",
          path: `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}`,
        },
        {
          label: "Analysis & Reports",
          path: `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/analysis`,
        },
      ]
    : [
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Client Database", path: "/advisor-portal/client-database" },
        {
          label: "Household Profile",
          path: `/advisor-portal/client-database/household-profile/${householdId}`,
        },
        {
          label: "Analysis & Reports",
          path: `/advisor-portal/client-database/household-profile/${householdId}/analysis`,
        },
      ];

  return (
    <SubPageLayout
      title="Analysis & Reports"
      subtitle="View household analysis and reports"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow mt-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
          Analysis & Reports
        </h2>

        {/* Performance Overview */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              Portfolio Performance
            </h3>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              +12.5% YTD
            </span>
          </div>
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Total Assets
              </label>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                $1.2M
              </div>
              <div className="text-sm text-green-600">
                ↑ 8.3% from last quarter
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                ROI
              </label>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                15.2%
              </div>
              <div className="text-sm text-green-600">↑ 2.1% from target</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Risk Score
              </label>
              <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                65/100
              </div>
              <div className="text-sm text-blue-600">Moderate</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Goal Progress
              </label>
              <div className="mt-1 text-2xl font-semibold text-green-600">
                85%
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">On Track</div>
            </div>
          </div>
        </div>

        {/* Investment Analysis */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
            Investment Analysis
          </h3>
          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Asset Allocation
              </h4>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Stocks</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      60%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: "60%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Bonds</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      30%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: "30%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Real Estate</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      5%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-600 h-2 rounded-full"
                      style={{ width: "5%" }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Cash</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      5%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gray-600 h-2 rounded-full"
                      style={{ width: "5%" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Financial Metrics */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
            Key Financial Metrics
          </h3>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Income Analysis
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Monthly Income</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    $12,500
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Monthly Expenses
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    $8,750
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Savings Rate</span>
                  <span className="text-sm font-medium text-green-600">
                    30%
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Retirement Projection
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Target Age</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">65</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Projected Portfolio
                  </span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    $3.2M
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Funding Status</span>
                  <span className="text-sm font-medium text-green-600">
                    85% Funded
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Reports */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              Generated Reports
            </h3>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Generate New Analysis
            </button>
          </div>
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Report Name
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Type
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Date Generated
                  </th>
                  <th className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-surface-default dark:bg-surface-default">
                <tr>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 dark:text-gray-100">
                    Q1 2024 Portfolio Analysis
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-500">
                    Performance Report
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-500">
                    2024-01-15
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-right">
                    <button className="text-blue-600 hover:text-blue-900 mr-4">
                      View
                    </button>
                    <button className="text-blue-600 hover:text-blue-900">
                      Download
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default AnalysisReports;
